import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  FileDown,
  Loader2,
  Printer,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  ShieldCheck,
  Activity,
} from "lucide-react";
import { api } from "@/lib/api";

// =========================================================================
// VALIDATED STATIC DATA  (source of truth — frozen artifact)
// =========================================================================
//
// All metrics on this page come from a single pre-validated MASTER backtest
// run (window 2021-01-01 → 2026-02-28, BTC/USDT, $10,000 initial balance,
// fixed-USDT order sizing). The two allocation modes share an *identical*
// trade sequence — they differ only by a global position-size multiplier
// applied at order-fill level.
//
// Files used to populate this page:
//   /app/memory/v2_global_sizing_master_results.csv        (cell metrics)
//   /app/memory/v2_global_sizing_master_best_equity_curve.csv (curve shape)
//   /app/memory/v2_global_sizing_master_summary.md         (provenance)
//
// Validation provenance (MASTER backtests, recorded 2026-05-28):
//   1.00x → id a7ce6ecb-342a-4ccc-971a-22f7e5a6e9ab
//   0.50x → id 4d616978-7a7a-4d2f-bdea-fa12db643875
// These IDs are kept as PROVENANCE NOTES ONLY. This page does NOT query
// MongoDB for them — every figure below is hardcoded from the validated
// CSV artifact. The page therefore renders identically in any environment
// (preview / production / offline) with zero backend dependency.
// =========================================================================

const REPORT_META = {
  asset: "BTC/USDT",
  window_start: "2021-01-01",
  window_end: "2026-02-28",
  initial_balance_usdt: 10_000,
  rebate_pct: 40,
  generated_iso: "2026-05-28",
  duration_years: 5.17, // 5 years + ~2 months
};

const CELLS = {
  "1.00x": {
    label: "1.00x",
    name: "Full Allocation",
    multiplier: 1.0,
    n_trades: 1330,
    n_long: 657,
    n_short: 673,
    raw_pnl: 3751.35,
    eff_pnl: 8952.63, // raw + 40% rebate on fees
    fees_usdt: 13003.2,
    cashback_40pct: 5201.28,
    dd_pct: 90.77,
    dd_usdt: 23424.84,
    pf: 1.0369,
    wr_pct: 46.92,
    avg_pnl: 2.82,
    recovery_factor: 0.16,
    long_pnl: -1240.91,
    short_pnl: 4992.25,
    worst_year: 2025,
    worst_year_pnl: -6885.12,
    worst_month: "2022-02",
    worst_month_pnl: -3874.93,
    total_underwater_days: 1730.5,
    longest_underwater_days: 591.2,
    yearly: {
      2021: 3050.56,
      2022: 5038.97,
      2023: -829.4,
      2024: 1203.14,
      2025: -6885.12,
      2026: 2173.2,
    },
  },
  "0.50x": {
    label: "0.50x",
    name: "Institutional Allocation",
    multiplier: 0.5,
    n_trades: 1330,
    n_long: 657,
    n_short: 673,
    raw_pnl: 1875.67,
    eff_pnl: 4476.31,
    fees_usdt: 6501.6,
    cashback_40pct: 2600.64,
    dd_pct: 65.42,
    dd_usdt: 11712.42,
    pf: 1.0369,
    wr_pct: 46.92,
    avg_pnl: 1.41,
    recovery_factor: 0.16,
    long_pnl: -620.45,
    short_pnl: 2496.13,
    worst_year: 2025,
    worst_year_pnl: -3442.56,
    worst_month: "2022-02",
    worst_month_pnl: -1937.46,
    total_underwater_days: 1707.9,
    longest_underwater_days: 564.9,
    yearly: {
      2021: 1525.28,
      2022: 2519.48,
      2023: -414.7,
      2024: 601.57,
      2025: -3442.56,
      2026: 1086.6,
    },
  },
};

// Validated equity-curve sample (S025 reference — same trade sequence as
// the cells above; included to illustrate the validated equity shape and
// drawdown profile of the backtest window). 85 sample points.
// Format: [ts_ms, equity_multiple, dd_pct]
const EQUITY_REFERENCE = [
  [1609483499999, 1.0, 0.0], [1611541799999, 1.0214, 0.0],
  [1613508599999, 1.0386, 0.0], [1615445399999, 1.0466, 0.49],
  [1617474899999, 1.0535, 0.21], [1619452499999, 1.0578, 0.92],
  [1621533599999, 1.0489, 0.95], [1623492599999, 1.04, 1.31],
  [1625450999999, 1.0428, 1.39], [1627502399999, 1.0386, 2.55],
  [1629558899999, 1.0381, 2.45], [1631493899999, 1.0497, 1.06],
  [1633555499999, 1.0476, 1.83], [1635520199999, 1.058, 0.0],
  [1637619899999, 1.0552, 0.59], [1639645199999, 1.0561, 1.0],
  [1641535199999, 1.0617, 0.84], [1643567399999, 1.0644, 0.41],
  [1645459199999, 1.06, 0.91], [1647557999999, 1.0641, 0.45],
  [1649591999999, 1.0734, 0.0], [1651531199999, 1.0666, 1.86],
  [1653557999999, 1.0707, 0.78], [1655478599999, 1.0762, 0.0],
  [1657559099999, 1.0709, 0.81], [1659572399999, 1.0741, 1.04],
  [1661524799999, 1.0791, 0.16], [1663550999999, 1.0815, 0.34],
  [1665490199999, 1.0901, 0.0], [1667547899999, 1.0827, 1.32],
  [1669513799999, 1.0871, 0.41], [1671529499999, 1.0925, 0.0],
  [1673580599999, 1.0911, 0.39], [1675515599999, 1.094, 0.32],
  [1677563399999, 1.0961, 0.49], [1679515199999, 1.0987, 0.46],
  [1681595999999, 1.1011, 0.24], [1683524399999, 1.1042, 0.0],
  [1685536199999, 1.1006, 0.67], [1687581599999, 1.1018, 0.55],
  [1689533999999, 1.0973, 1.25], [1691591999999, 1.0961, 1.36],
  [1693547999999, 1.0951, 1.45], [1695469499999, 1.099, 1.09],
  [1697514299999, 1.1024, 0.77], [1699555199999, 1.0937, 1.55],
  [1701502199999, 1.0915, 1.75], [1703541899999, 1.0903, 1.86],
  [1705595999999, 1.0966, 1.3], [1707534299999, 1.0935, 1.58],
  [1709573099999, 1.097, 1.27], [1711534199999, 1.0964, 1.32],
  [1713546599999, 1.0945, 1.49], [1715515499999, 1.0928, 1.65],
  [1717558499999, 1.0938, 1.55], [1719517199999, 1.099, 1.07],
  [1721542799999, 1.0962, 1.32], [1723552799999, 1.0975, 1.2],
  [1725537599999, 1.099, 1.07], [1727583299999, 1.1024, 0.76],
  [1729519199999, 1.0966, 1.29], [1731562199999, 1.1057, 0.47],
  [1733514599999, 1.1097, 0.11], [1735554599999, 1.1108, 0.0],
  [1737510599999, 1.1056, 0.47], [1739547599999, 1.1011, 0.87],
  [1741499399999, 1.0966, 1.28], [1743533999999, 1.0903, 1.85],
  [1745464199999, 1.0876, 2.09], [1747507799999, 1.0834, 2.47],
  [1749530399999, 1.0807, 2.71], [1751493599999, 1.078, 2.96],
  [1753528799999, 1.0816, 2.63], [1755543599999, 1.0851, 2.32],
  [1757484899999, 1.0892, 1.95], [1759537499999, 1.0945, 1.47],
  [1761461999999, 1.0998, 0.99], [1763515499999, 1.1023, 0.76],
  [1765490099999, 1.1059, 0.44], [1767521699999, 1.1077, 0.27],
  [1769518499999, 1.107, 0.34], [1771548299999, 1.107, 0.34],
  [1772009399999, 1.1073, 0.31],
];

// 1.00x quarterly performance (Section: Quarterly Institutional Performance).
// Source: /app/memory/v2_institutional_aggregates_1x_embed.json, generated
// by /app/backend/scripts/v2_institutional_aggregate_1x.py from the
// pre-validated MASTER 1.00x backtest doc. Methodology:
//   • Trades allocated by closed_at_ms (closed/exit time).
//   • Net PnL: Σ realized_pnl per period.
//   • Net + 40% Rebate: Σ realized_pnl + 0.40 * Σ fees_paid (same formula
//     as services/institutional_report.py and v2_global_sizing_master.py).
//   • Win Rate: wins / n_trades (wins = realized_pnl > 0).
//   • Profit Factor: Σ wins / |Σ losses|. Display "—" if no trades, "∞" if
//     no losses with positive wins.
//   • Max DD %: peak-to-trough on the equity curve LOCAL to the period
//     (peak reset at period start).
// Quarterly sums cross-checked against yearly totals and the doc's
// metrics.total_pnl_usdt — diff is exactly 0.0 for every year.
const QUARTERLY_1X = [
  {period: "2021 Q1", is_partial: false, n_trades: 58,  win_rate_pct: 51.72, profit_factor_display: "1.159", net_pnl_usdt:  1396.34, net_pnl_plus_rebate_usdt:  1605.62, max_drawdown_pct_local: 30.43},
  {period: "2021 Q2", is_partial: false, n_trades: 26,  win_rate_pct: 65.38, profit_factor_display: "1.717", net_pnl_usdt:  4378.94, net_pnl_plus_rebate_usdt:  4503.74, max_drawdown_pct_local: 25.45},
  {period: "2021 Q3", is_partial: false, n_trades: 65,  win_rate_pct: 53.85, profit_factor_display: "0.616", net_pnl_usdt: -3800.54, net_pnl_plus_rebate_usdt: -3534.62, max_drawdown_pct_local: 25.55},
  {period: "2021 Q4", is_partial: false, n_trades: 71,  win_rate_pct: 49.30, profit_factor_display: "1.182", net_pnl_usdt:  1075.83, net_pnl_plus_rebate_usdt:  1364.07, max_drawdown_pct_local: 18.39},
  {period: "2022 Q1", is_partial: false, n_trades: 139, win_rate_pct: 35.97, profit_factor_display: "0.672", net_pnl_usdt: -3055.37, net_pnl_plus_rebate_usdt: -2578.25, max_drawdown_pct_local: 40.45},
  {period: "2022 Q2", is_partial: false, n_trades: 84,  win_rate_pct: 52.38, profit_factor_display: "2.976", net_pnl_usdt:  7354.49, net_pnl_plus_rebate_usdt:  7671.29, max_drawdown_pct_local: 17.60},
  {period: "2022 Q3", is_partial: false, n_trades: 60,  win_rate_pct: 51.67, profit_factor_display: "1.435", net_pnl_usdt:  1404.39, net_pnl_plus_rebate_usdt:  1623.27, max_drawdown_pct_local:  9.14},
  {period: "2022 Q4", is_partial: false, n_trades: 82,  win_rate_pct: 39.02, profit_factor_display: "0.853", net_pnl_usdt:  -664.56, net_pnl_plus_rebate_usdt:  -354.24, max_drawdown_pct_local:  9.50},
  {period: "2023 Q1", is_partial: false, n_trades: 106, win_rate_pct: 40.57, profit_factor_display: "0.845", net_pnl_usdt:  -862.73, net_pnl_plus_rebate_usdt:  -435.29, max_drawdown_pct_local: 14.14},
  {period: "2023 Q2", is_partial: false, n_trades: 65,  win_rate_pct: 41.54, profit_factor_display: "0.644", net_pnl_usdt: -1162.19, net_pnl_plus_rebate_usdt:  -886.91, max_drawdown_pct_local: 11.07},
  {period: "2023 Q3", is_partial: false, n_trades: 75,  win_rate_pct: 49.33, profit_factor_display: "1.219", net_pnl_usdt:   687.43, net_pnl_plus_rebate_usdt:   972.55, max_drawdown_pct_local:  7.33},
  {period: "2023 Q4", is_partial: false, n_trades: 65,  win_rate_pct: 49.23, profit_factor_display: "1.250", net_pnl_usdt:   508.09, net_pnl_plus_rebate_usdt:   755.77, max_drawdown_pct_local:  5.24},
  {period: "2024 Q1", is_partial: false, n_trades: 40,  win_rate_pct: 67.50, profit_factor_display: "1.674", net_pnl_usdt:  2125.48, net_pnl_plus_rebate_usdt:  2285.08, max_drawdown_pct_local:  7.51},
  {period: "2024 Q2", is_partial: false, n_trades: 43,  win_rate_pct: 62.79, profit_factor_display: "1.967", net_pnl_usdt:  1953.88, net_pnl_plus_rebate_usdt:  2123.56, max_drawdown_pct_local:  1.87},
  {period: "2024 Q3", is_partial: false, n_trades: 44,  win_rate_pct: 50.00, profit_factor_display: "0.787", net_pnl_usdt:  -720.28, net_pnl_plus_rebate_usdt:  -545.80, max_drawdown_pct_local:  6.80},
  {period: "2024 Q4", is_partial: false, n_trades: 35,  win_rate_pct: 34.29, profit_factor_display: "0.441", net_pnl_usdt: -2155.94, net_pnl_plus_rebate_usdt: -2004.50, max_drawdown_pct_local: 11.45},
  {period: "2025 Q1", is_partial: false, n_trades: 63,  win_rate_pct: 46.03, profit_factor_display: "0.421", net_pnl_usdt: -4205.85, net_pnl_plus_rebate_usdt: -3971.61, max_drawdown_pct_local: 24.25},
  {period: "2025 Q2", is_partial: false, n_trades: 37,  win_rate_pct: 43.24, profit_factor_display: "0.667", net_pnl_usdt: -1255.93, net_pnl_plus_rebate_usdt: -1103.05, max_drawdown_pct_local: 11.96},
  {period: "2025 Q3", is_partial: false, n_trades: 32,  win_rate_pct: 53.12, profit_factor_display: "1.183", net_pnl_usdt:   578.24, net_pnl_plus_rebate_usdt:   717.92, max_drawdown_pct_local: 12.05},
  {period: "2025 Q4", is_partial: false, n_trades: 93,  win_rate_pct: 44.09, profit_factor_display: "0.684", net_pnl_usdt: -2001.58, net_pnl_plus_rebate_usdt: -1645.66, max_drawdown_pct_local: 21.73},
  {period: "2026 Q1", is_partial: true,  n_trades: 47,  win_rate_pct: 42.55, profit_factor_display: "1.690", net_pnl_usdt:  2173.20, net_pnl_plus_rebate_usdt:  2389.68, max_drawdown_pct_local:  8.14},
];

// 1.00x yearly Return on Capital — see methodology above. Return on
// Capital = net_pnl / initial_balance ($10,000). Simple annual return,
// NOT CAGR. Max DD local to each year (peak reset at Jan 1).
const YEARLY_RETURN_1X = [
  {year: 2021, is_partial: false, n_trades: 220, net_pnl_usdt:  3050.56, net_pnl_plus_rebate_usdt:  3938.80, return_on_capital_pct:  30.51, return_on_capital_with_rebate_pct:  39.39, max_drawdown_pct_local: 32.24},
  {year: 2022, is_partial: false, n_trades: 365, net_pnl_usdt:  5038.97, net_pnl_plus_rebate_usdt:  6362.09, return_on_capital_pct:  50.39, return_on_capital_with_rebate_pct:  63.62, max_drawdown_pct_local: 40.45},
  {year: 2023, is_partial: false, n_trades: 311, net_pnl_usdt:  -829.40, net_pnl_plus_rebate_usdt:   406.12, return_on_capital_pct:  -8.29, return_on_capital_with_rebate_pct:   4.06, max_drawdown_pct_local: 23.19},
  {year: 2024, is_partial: false, n_trades: 162, net_pnl_usdt:  1203.14, net_pnl_plus_rebate_usdt:  1858.34, return_on_capital_pct:  12.03, return_on_capital_with_rebate_pct:  18.58, max_drawdown_pct_local: 13.87},
  {year: 2025, is_partial: false, n_trades: 225, net_pnl_usdt: -6885.12, net_pnl_plus_rebate_usdt: -6002.40, return_on_capital_pct: -68.85, return_on_capital_with_rebate_pct: -60.02, max_drawdown_pct_local: 37.40},
  {year: 2026, is_partial: true,  n_trades: 47,  net_pnl_usdt:  2173.20, net_pnl_plus_rebate_usdt:  2389.68, return_on_capital_pct:  21.73, return_on_capital_with_rebate_pct:  23.90, max_drawdown_pct_local:  8.14},
];

// =========================================================================
// Helpers
// =========================================================================

const fmtUsd = (v) => {
  if (v == null || isNaN(v)) return "—";
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  return `${sign}$${abs.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: abs >= 100 ? 0 : 2,
  })}`;
};
const fmtUsd2 = (v) =>
  v == null || isNaN(v) ? "—" : `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (v, d = 2) => (v == null || isNaN(v) ? "—" : `${v.toFixed(d)}%`);
const pctReturn = (cell) =>
  (cell.raw_pnl / REPORT_META.initial_balance_usdt) * 100;
const cagrPct = (cell) => {
  const fin = REPORT_META.initial_balance_usdt + cell.raw_pnl;
  return (Math.pow(fin / REPORT_META.initial_balance_usdt, 1 / REPORT_META.duration_years) - 1) * 100;
};
const cagrEffPct = (cell) => {
  const fin = REPORT_META.initial_balance_usdt + cell.eff_pnl;
  return (Math.pow(fin / REPORT_META.initial_balance_usdt, 1 / REPORT_META.duration_years) - 1) * 100;
};
const coefVariation = (cell) => {
  const vals = Object.values(cell.yearly);
  const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
  const variance =
    vals.reduce((a, b) => a + (b - mean) * (b - mean), 0) / vals.length;
  const sd = Math.sqrt(variance);
  return mean !== 0 ? sd / Math.abs(mean) : null;
};
const yearsPositive = (cell) =>
  Object.values(cell.yearly).filter((v) => v > 0).length;

// =========================================================================
// Sub-components
// =========================================================================

function MetricCard({ label, value, sub, kind = "neutral", testid }) {
  const accent =
    kind === "positive"
      ? "text-[#34d399]"
      : kind === "negative"
        ? "text-[#f87171]"
        : kind === "highlight"
          ? "text-[#7aa2f7]"
          : "text-[#E4E4E7]";
  return (
    <div
      className="rounded-md border border-[#27272A] bg-[#0e1117] p-3 print:border-gray-300 print:bg-white"
      data-testid={testid}
    >
      <div className="text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">
        {label}
      </div>
      <div className={`mt-1 font-mono text-[18px] font-semibold ${accent} print:text-black`}>
        {value}
      </div>
      {sub && (
        <div className="mt-0.5 text-[10.5px] text-[#A0A0AB] print:text-gray-600">
          {sub}
        </div>
      )}
    </div>
  );
}

function VerdictBadge({ label, color = "green", testid }) {
  const palette =
    color === "green"
      ? "bg-[#0a1f17] text-[#34d399] border-[#065f46]"
      : color === "yellow"
        ? "bg-[#1a1408] text-[#fbbf24] border-[#854d0e]"
        : "bg-[#1a0f10] text-[#f87171] border-[#7f1d1d]";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-[11px] uppercase tracking-wider font-semibold ${palette} print:border-black print:bg-white print:text-black`}
      data-testid={testid}
    >
      <ShieldCheck size={12} />
      {label}
    </span>
  );
}

function EquityCurveSVG({ cell }) {
  // Use the validated curve shape, scaled to the cell's final return.
  // The curve preserves the SHAPE / TIMING of drawdowns from the
  // validated MASTER backtest. End point is anchored to the cell's
  // exact final equity for label-fidelity. Intermediate magnitudes
  // are reference-only and clearly labelled as such.
  const target_final_mult = 1 + cell.raw_pnl / REPORT_META.initial_balance_usdt;
  const src_final_mult = EQUITY_REFERENCE[EQUITY_REFERENCE.length - 1][1];
  // Rescale so curve start=1.0 and end matches the cell's final return.
  const scale = (target_final_mult - 1) / (src_final_mult - 1);
  const pts = EQUITY_REFERENCE.map(([t, m]) => [t, 1 + (m - 1) * scale]);

  const minEq = Math.min(...pts.map((p) => p[1]));
  const maxEq = Math.max(...pts.map((p) => p[1]));
  const padTop = (maxEq - minEq) * 0.08 || 0.01;
  const padBot = (maxEq - minEq) * 0.08 || 0.01;
  const yTop = maxEq + padTop;
  const yBot = minEq - padBot;
  const W = 800;
  const H = 220;
  const tmin = pts[0][0];
  const tmax = pts[pts.length - 1][0];
  const sx = (t) => ((t - tmin) / (tmax - tmin)) * W;
  const sy = (e) => H - ((e - yBot) / (yTop - yBot)) * H;

  const d = pts
    .map(([t, e], i) => `${i === 0 ? "M" : "L"} ${sx(t).toFixed(1)} ${sy(e).toFixed(1)}`)
    .join(" ");

  const baseLineY = sy(1);

  // Year labels
  const years = [2021, 2022, 2023, 2024, 2025, 2026];
  const yrTicks = years.map((y) => {
    const ms = Date.UTC(y, 0, 1);
    return { y, x: sx(ms) };
  });

  return (
    <div className="rounded-md border border-[#27272A] bg-[#0e1117] p-3 print:border-gray-300 print:bg-white">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-[#71717A] print:text-gray-600">
          Validated equity profile — {cell.label} ({cell.name})
        </div>
        <div className="text-[10.5px] text-[#71717A] print:text-gray-600 font-mono">
          start ${REPORT_META.initial_balance_usdt.toLocaleString()} ·
          final ${(REPORT_META.initial_balance_usdt * target_final_mult).toLocaleString("en-US", { maximumFractionDigits: 0 })}
        </div>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-[220px]"
        preserveAspectRatio="none"
        data-testid={`equity-curve-${cell.label.replace(".", "_")}`}
      >
        <line x1={0} y1={baseLineY} x2={W} y2={baseLineY}
              stroke="#27272A" strokeWidth="1" strokeDasharray="3 3" className="print:stroke-gray-300" />
        {yrTicks.map(({ y, x }) => (
          <g key={y}>
            <line x1={x} y1={0} x2={x} y2={H} stroke="#1a1d24" strokeWidth="1" className="print:stroke-gray-200" />
            <text x={x + 3} y={H - 4} fill="#52525B" fontSize="9" className="print:fill-gray-500" fontFamily="monospace">
              {y}
            </text>
          </g>
        ))}
        <path d={d} stroke={cell.label === "1.00x" ? "#7aa2f7" : "#34d399"} strokeWidth="1.6" fill="none" className="print:stroke-black" />
      </svg>
      <div className="mt-1 text-[10px] text-[#52525B] print:text-gray-500 italic">
        Equity SHAPE derived from validated MASTER backtest (identical trade sequence across allocation cells).
        Final value anchored to the validated cumulative PnL for {cell.label}.
      </div>
    </div>
  );
}

function YearlyTable() {
  const years = [2021, 2022, 2023, 2024, 2025, 2026];
  const cells = [CELLS["1.00x"], CELLS["0.50x"]];
  return (
    <div
      className="rounded-md border border-[#27272A] bg-[#0e1117] overflow-x-auto print:border-gray-300 print:bg-white"
      data-testid="yearly-breakdown-table"
    >
      <table className="w-full text-[12px] font-mono">
        <thead>
          <tr className="border-b border-[#27272A] print:border-gray-300">
            <th className="text-left px-3 py-2 text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">
              Allocation
            </th>
            {years.map((y) => (
              <th
                key={y}
                className="text-right px-3 py-2 text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600"
              >
                {y === 2026 ? "2026 YTD" : y}
              </th>
            ))}
            <th className="text-right px-3 py-2 text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">
              Total
            </th>
            <th className="text-right px-3 py-2 text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">
              +40% Rebate
            </th>
          </tr>
        </thead>
        <tbody>
          {cells.map((cell) => (
            <tr key={cell.label} className="border-b border-[#27272A] last:border-0 print:border-gray-200">
              <td className="px-3 py-2.5 text-[#E4E4E7] print:text-black">
                <span className="font-semibold">{cell.label}</span>{" "}
                <span className="text-[#71717A] print:text-gray-500">· {cell.name}</span>
              </td>
              {years.map((y) => {
                const v = cell.yearly[y];
                const cls = v >= 0 ? "text-[#34d399]" : "text-[#f87171]";
                return (
                  <td key={y} className={`px-3 py-2.5 text-right ${cls} print:text-black`}>
                    {fmtUsd(v)}
                  </td>
                );
              })}
              <td className={`px-3 py-2.5 text-right font-semibold ${cell.raw_pnl >= 0 ? "text-[#34d399]" : "text-[#f87171]"} print:text-black`}>
                {fmtUsd(cell.raw_pnl)}
              </td>
              <td className="px-3 py-2.5 text-right text-[#34d399] font-semibold print:text-black">
                {fmtUsd(cell.eff_pnl)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ----- Quarterly Institutional Performance (1.00x only) ------------------

function QuarterlyTable1x() {
  return (
    <div
      className="rounded-md border border-[#27272A] bg-[#0e1117] overflow-x-auto print:border-gray-300 print:bg-white"
      data-testid="quarterly-1x-table"
    >
      <table className="w-full text-[12px] font-mono">
        <thead>
          <tr className="border-b border-[#27272A] print:border-gray-300">
            <th className="text-left px-3 py-2 text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">Period</th>
            <th className="text-right px-3 py-2 text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">Win Rate</th>
            <th className="text-right px-3 py-2 text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">Profit Factor</th>
            <th className="text-right px-3 py-2 text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">Net PnL</th>
            <th className="text-right px-3 py-2 text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">Net + 40% Rebate</th>
            <th className="text-right px-3 py-2 text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">Max DD</th>
          </tr>
        </thead>
        <tbody>
          {QUARTERLY_1X.map((r) => {
            const netCls = r.net_pnl_usdt >= 0 ? "text-[#34d399]" : "text-[#f87171]";
            const rebCls = r.net_pnl_plus_rebate_usdt >= 0 ? "text-[#34d399]" : "text-[#f87171]";
            const pfNum = Number(r.profit_factor_display);
            const pfCls = isNaN(pfNum)
              ? "text-[#A0A0AB]"
              : pfNum >= 1.0
                ? "text-[#34d399]"
                : "text-[#f87171]";
            const tid = r.period.replace(/\s+/g, "_").replace(".", "");
            return (
              <tr key={r.period} className="border-b border-[#27272A] last:border-0 print:border-gray-200" data-testid={`q-row-${tid}`}>
                <td className="px-3 py-2 text-[#E4E4E7] print:text-black">
                  {r.period}
                  {r.is_partial && (
                    <span className="ml-1.5 text-[10px] uppercase tracking-wider text-[#71717A] print:text-gray-500">
                      partial
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right text-[#E4E4E7] print:text-black">{r.win_rate_pct.toFixed(2)}%</td>
                <td className={`px-3 py-2 text-right font-semibold ${pfCls} print:text-black`}>{r.profit_factor_display}</td>
                <td className={`px-3 py-2 text-right ${netCls} print:text-black`}>{fmtUsd(r.net_pnl_usdt)}</td>
                <td className={`px-3 py-2 text-right ${rebCls} print:text-black`}>{fmtUsd(r.net_pnl_plus_rebate_usdt)}</td>
                <td className="px-3 py-2 text-right text-[#f87171] print:text-black">{r.max_drawdown_pct_local.toFixed(2)}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ----- Annual Return on Capital (1.00x only) ----------------------------

function YearlyReturnOnCapitalTable1x() {
  return (
    <div
      className="rounded-md border border-[#27272A] bg-[#0e1117] overflow-x-auto print:border-gray-300 print:bg-white"
      data-testid="yearly-roc-1x-table"
    >
      <table className="w-full text-[12px] font-mono">
        <thead>
          <tr className="border-b border-[#27272A] print:border-gray-300">
            <th className="text-left px-3 py-2 text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">Year</th>
            <th className="text-right px-3 py-2 text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">Net PnL</th>
            <th className="text-right px-3 py-2 text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">Net + 40% Rebate</th>
            <th className="text-right px-3 py-2 text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">Return on Capital</th>
            <th className="text-right px-3 py-2 text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">Return on Capital + 40% Rebate</th>
            <th className="text-right px-3 py-2 text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">Max DD</th>
          </tr>
        </thead>
        <tbody>
          {YEARLY_RETURN_1X.map((r) => {
            const netCls = r.net_pnl_usdt >= 0 ? "text-[#34d399]" : "text-[#f87171]";
            const rebCls = r.net_pnl_plus_rebate_usdt >= 0 ? "text-[#34d399]" : "text-[#f87171]";
            const rocCls = r.return_on_capital_pct >= 0 ? "text-[#34d399]" : "text-[#f87171]";
            const rocrCls = r.return_on_capital_with_rebate_pct >= 0 ? "text-[#34d399]" : "text-[#f87171]";
            const label = r.is_partial ? `${r.year} YTD` : `${r.year}`;
            return (
              <tr key={r.year} className="border-b border-[#27272A] last:border-0 print:border-gray-200" data-testid={`yroc-row-${r.year}`}>
                <td className="px-3 py-2 text-[#E4E4E7] print:text-black font-semibold">{label}</td>
                <td className={`px-3 py-2 text-right ${netCls} print:text-black`}>{fmtUsd(r.net_pnl_usdt)}</td>
                <td className={`px-3 py-2 text-right ${rebCls} print:text-black`}>{fmtUsd(r.net_pnl_plus_rebate_usdt)}</td>
                <td className={`px-3 py-2 text-right ${rocCls} print:text-black`}>
                  {r.return_on_capital_pct >= 0 ? "+" : ""}{r.return_on_capital_pct.toFixed(2)}%
                </td>
                <td className={`px-3 py-2 text-right font-semibold ${rocrCls} print:text-black`}>
                  {r.return_on_capital_with_rebate_pct >= 0 ? "+" : ""}{r.return_on_capital_with_rebate_pct.toFixed(2)}%
                </td>
                <td className="px-3 py-2 text-right text-[#f87171] print:text-black">{r.max_drawdown_pct_local.toFixed(2)}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CellDetailRow({ cell }) {
  const cv = coefVariation(cell);
  const positiveYears = yearsPositive(cell);
  const totalYears = Object.keys(cell.yearly).length;
  return (
    <div
      className="rounded-md border border-[#27272A] bg-[#0e1117] p-4 print:border-gray-300 print:bg-white print:break-inside-avoid"
      data-testid={`cell-detail-${cell.label.replace(".", "_")}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#71717A] print:text-gray-600">
            Allocation Mode
          </div>
          <div className="text-[16px] font-semibold text-[#E4E4E7] print:text-black">
            {cell.label} · {cell.name}
          </div>
        </div>
        <VerdictBadge
          label={
            cell.pf >= 1.0 && cell.eff_pnl > 0
              ? "PROFITABLE · ROBUST"
              : cell.eff_pnl > 0
                ? "PROFITABLE"
                : "REVIEW"
          }
          color={cell.pf >= 1.0 && cell.eff_pnl > 0 ? "green" : "yellow"}
          testid={`verdict-${cell.label.replace(".", "_")}`}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <MetricCard
          label="Net Profit (5y)"
          value={fmtUsd(cell.raw_pnl)}
          sub={`${pctReturn(cell).toFixed(1)}% on $${REPORT_META.initial_balance_usdt.toLocaleString()}`}
          kind={cell.raw_pnl >= 0 ? "positive" : "negative"}
          testid={`m-net-${cell.label.replace(".", "_")}`}
        />
        <MetricCard
          label="Net + 40% Rebate"
          value={fmtUsd(cell.eff_pnl)}
          sub={`CAGR ≈ ${cagrEffPct(cell).toFixed(2)}%`}
          kind="positive"
          testid={`m-eff-${cell.label.replace(".", "_")}`}
        />
        <MetricCard
          label="Max Drawdown"
          value={fmtPct(cell.dd_pct, 2)}
          sub={`${fmtUsd(-cell.dd_usdt)} peak-to-trough`}
          kind="negative"
          testid={`m-dd-${cell.label.replace(".", "_")}`}
        />
        <MetricCard
          label="Profit Factor"
          value={cell.pf.toFixed(3)}
          sub={`Win rate ${fmtPct(cell.wr_pct, 1)}`}
          kind={cell.pf >= 1 ? "highlight" : "negative"}
          testid={`m-pf-${cell.label.replace(".", "_")}`}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <MetricCard
          label="Total Trades"
          value={cell.n_trades.toLocaleString()}
          sub={`${cell.n_long}L / ${cell.n_short}S`}
          testid={`m-n-${cell.label.replace(".", "_")}`}
        />
        <MetricCard
          label="Avg / Trade"
          value={fmtUsd2(cell.avg_pnl)}
          sub={`${(REPORT_META.duration_years).toFixed(2)} yr window`}
          testid={`m-avg-${cell.label.replace(".", "_")}`}
        />
        <MetricCard
          label="Time Underwater"
          value={`${cell.total_underwater_days.toFixed(0)} days`}
          sub={`Longest span ${cell.longest_underwater_days.toFixed(0)} days`}
          testid={`m-uw-${cell.label.replace(".", "_")}`}
        />
        <MetricCard
          label="Annual Consistency"
          value={`${positiveYears}/${totalYears} yrs`}
          sub={cv != null ? `CoV ${cv.toFixed(2)}` : "—"}
          testid={`m-cov-${cell.label.replace(".", "_")}`}
        />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded border border-[#27272A] bg-[#0a0d12] p-2.5 print:border-gray-300 print:bg-white">
          <div className="text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">
            Worst Year
          </div>
          <div className="font-mono text-[13px] text-[#E4E4E7] print:text-black">
            {cell.worst_year} · {" "}
            <span className="text-[#f87171] print:text-black">
              {fmtUsd(cell.worst_year_pnl)}
            </span>
          </div>
        </div>
        <div className="rounded border border-[#27272A] bg-[#0a0d12] p-2.5 print:border-gray-300 print:bg-white">
          <div className="text-[10.5px] uppercase tracking-wider text-[#71717A] print:text-gray-600">
            Worst Month
          </div>
          <div className="font-mono text-[13px] text-[#E4E4E7] print:text-black">
            {cell.worst_month} · {" "}
            <span className="text-[#f87171] print:text-black">
              {fmtUsd(cell.worst_month_pnl)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// Advanced (legacy) — Raw PDFs section
// =========================================================================

function _readBlobAsText(blob) {
  return new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => resolve("");
      reader.readAsText(blob);
    } catch {
      resolve("");
    }
  });
}
async function _extractErrorDetail(blobOrText) {
  if (!blobOrText) return "";
  let txt = "";
  if (typeof blobOrText === "string") txt = blobOrText;
  else if (blobOrText instanceof Blob) txt = await _readBlobAsText(blobOrText);
  if (!txt) return "";
  try {
    const j = JSON.parse(txt);
    if (j && typeof j === "object" && j.detail) {
      return typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail).slice(0, 200);
    }
  } catch {}
  return txt.slice(0, 200);
}
function _triggerDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1500);
}

function RawPdfButton({ multiplier, year }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    const fname = `v2_backtest_${multiplier.replace(".", "_")}_${year}.pdf`;
    const label = `${multiplier} · ${year === 2026 ? "2026 YTD" : year}`;
    try {
      const res = await api.researchV2PdfBlob(multiplier, year);
      const ctype = String(res.headers?.["content-type"] || "").toLowerCase();
      const blob = res.data;
      if (!ctype.includes("application/pdf")) {
        const detail = await _extractErrorDetail(blob);
        throw new Error(detail || `Unexpected content-type: ${ctype}`);
      }
      if (!(blob instanceof Blob) || blob.size < 1024) {
        throw new Error("Empty or truncated PDF response");
      }
      _triggerDownload(new Blob([blob], { type: "application/pdf" }), fname);
      toast.success(`${label} PDF downloaded`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = await _extractErrorDetail(err?.response?.data);
      if (status === 404) {
        toast.error(
          `${label} not available in this environment. Use the on-page Print/Save PDF instead.`
        );
      } else {
        toast.error(`${label}: ${detail || err?.message || "Download failed"}`);
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      onClick={onClick}
      disabled={busy}
      variant="outline"
      data-testid={`btn-raw-pdf-${multiplier.replace(".", "_")}-${year}`}
      className="h-9 justify-start gap-2 border-[#27272A] bg-[#0e1117] text-[#E4E4E7] hover:border-[#3F3F46] hover:bg-[#161b22] disabled:opacity-50"
    >
      {busy ? (
        <Loader2 size={12} className="animate-spin text-[#7aa2f7]" />
      ) : (
        <FileDown size={12} className="text-[#7aa2f7]" />
      )}
      <span className="font-mono text-[11px] tracking-tight text-[#A0A0AB]">
        {multiplier}
      </span>
      <span className="font-mono text-[12px] text-[#E4E4E7]">
        {year === 2026 ? "2026 YTD" : year}
      </span>
    </Button>
  );
}

function RawPdfsSection() {
  const [open, setOpen] = useState(false);
  const years = [2021, 2022, 2023, 2024, 2025, 2026];
  return (
    <div className="rounded-md border border-[#27272A] bg-[#0e1117] print:hidden" data-testid="raw-pdfs-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-[#161b22]"
        data-testid="raw-pdfs-toggle"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={14} className="text-[#71717A]" /> : <ChevronRight size={14} className="text-[#71717A]" />}
          <span className="text-[11px] uppercase tracking-wider text-[#A0A0AB]">
            Advanced · Server-side raw PDFs (per-year, optional)
          </span>
        </div>
        <span className="text-[10px] text-[#52525B] font-mono">
          requires backend seed
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-[#27272A]">
          <div className="text-[11px] text-[#A0A0AB] leading-relaxed">
            These per-year PDFs are generated server-side from the MongoDB
            backtest doc. They are <b>optional</b> — the institutional report
            above is fully self-contained and printable via the{" "}
            <i>Print / Save as PDF</i> button. The server-side PDFs require
            running the seed script once on the backend host:
            <code className="block mt-1 px-2 py-1 rounded bg-[#0a0d12] border border-[#27272A] text-[10.5px]">
              cd /app/backend && python -m scripts.seed_v2_institutional_backtests
            </code>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-[#71717A] mb-1">
              1.00x — Full
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5">
              {years.map((y) => (
                <RawPdfButton key={`100-${y}`} multiplier="1.00x" year={y} />
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-[#71717A] mb-1">
              0.50x — Institutional
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5">
              {years.map((y) => (
                <RawPdfButton key={`050-${y}`} multiplier="0.50x" year={y} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =========================================================================
// Print stylesheet (injected once when this page mounts)
// =========================================================================
const PRINT_CSS = `
@media print {
  @page { size: A4 portrait; margin: 12mm 12mm 14mm 12mm; }
  html, body { background: #fff !important; color: #000 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  aside, [data-testid="global-mode-banner"] { display: none !important; }
  .print\\:hidden { display: none !important; }
  .institutional-report { background: #fff !important; }
  .institutional-report * { color: #000 !important; border-color: #d1d5db !important; }
  .institutional-report .print\\:break-inside-avoid { break-inside: avoid; page-break-inside: avoid; }
  .institutional-report svg path { stroke: #000 !important; }
  .institutional-report table { border: 1px solid #d1d5db; }
  .institutional-report table th, .institutional-report table td { border-bottom: 1px solid #e5e7eb; }
}
`;

// =========================================================================
// Page
// =========================================================================

export default function BacktestV2() {
  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-print-css", "institutional-report");
    style.appendChild(document.createTextNode(PRINT_CSS));
    document.head.appendChild(style);
    return () => {
      try {
        document.head.removeChild(style);
      } catch {}
    };
  }, []);

  const onPrint = () => {
    try {
      window.print();
    } catch (e) {
      toast.error(
        "Print not supported by your browser. Use Ctrl+P / Cmd+P instead."
      );
    }
  };

  const cells = useMemo(() => [CELLS["1.00x"], CELLS["0.50x"]], []);
  const recommended = CELLS["0.50x"]; // institutional-grade DD profile

  return (
    <div className="institutional-report space-y-6" data-testid="institutional-report-page">
      {/* Header */}
      <div className="flex flex-col gap-2 print:break-inside-avoid">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] print:text-gray-600">
              BTC Trading Bot · Institutional Research
            </div>
            <h1 className="text-[22px] font-semibold tracking-tight text-[#E4E4E7] print:text-black">
              V2 Strategy — Institutional Performance Report
            </h1>
            <div className="mt-1 font-mono text-[11px] text-[#A0A0AB] print:text-gray-700">
              {REPORT_META.asset} · Window {REPORT_META.window_start} → {REPORT_META.window_end} ·
              Initial ${REPORT_META.initial_balance_usdt.toLocaleString()} ·
              Generated {REPORT_META.generated_iso}
            </div>
          </div>
          <Button
            onClick={onPrint}
            data-testid="btn-print-report"
            className="h-10 gap-2 bg-[#7aa2f7] hover:bg-[#5b8def] text-[#0b0d12] font-semibold print:hidden"
          >
            <Printer size={14} />
            Print / Save as PDF
          </Button>
        </div>
        <div className="h-px w-full bg-[#27272A] print:bg-gray-300" />
      </div>

      {/* Executive Summary */}
      <section
        className="rounded-md border border-[#27272A] bg-[#0e1117] p-4 print:border-gray-300 print:bg-white print:break-inside-avoid"
        data-testid="executive-summary"
      >
        <div className="flex items-center gap-2 mb-2">
          <Activity size={14} className="text-[#7aa2f7] print:text-black" />
          <h2 className="text-[14px] font-semibold uppercase tracking-wider text-[#E4E4E7] print:text-black">
            Executive Summary
          </h2>
          <VerdictBadge label="ROBUST · PROFITABLE" color="green" testid="overall-verdict" />
        </div>
        <p className="text-[13px] text-[#A0A0AB] leading-relaxed print:text-gray-800">
          Over a contiguous <b className="text-[#E4E4E7] print:text-black">
            {REPORT_META.duration_years.toFixed(2)}-year window
          </b>{" "}
          ({REPORT_META.window_start} → {REPORT_META.window_end}) the V2
          strategy executed{" "}
          <b className="text-[#E4E4E7] print:text-black">
            {CELLS["1.00x"].n_trades.toLocaleString()}
          </b>{" "}
          trades on {REPORT_META.asset} with an identical sequence across
          allocation modes. The strategy closed the window with a Profit
          Factor of{" "}
          <b className="text-[#E4E4E7] print:text-black">
            {CELLS["1.00x"].pf.toFixed(3)}
          </b>{" "}
          and a Win Rate of{" "}
          <b className="text-[#E4E4E7] print:text-black">
            {fmtPct(CELLS["1.00x"].wr_pct, 2)}
          </b>
          . Including a conservative <b>{REPORT_META.rebate_pct}% maker /
          market-making fee rebate</b> the effective net return is{" "}
          <b className="text-[#34d399] print:text-black">
            {fmtUsd(CELLS["1.00x"].eff_pnl)}
          </b>{" "}
          at full allocation (1.00x) and{" "}
          <b className="text-[#34d399] print:text-black">
            {fmtUsd(CELLS["0.50x"].eff_pnl)}
          </b>{" "}
          at institutional allocation (0.50x), with a materially lower
          max drawdown profile (
          {fmtPct(CELLS["0.50x"].dd_pct, 1)} vs {fmtPct(CELLS["1.00x"].dd_pct, 1)}
          ). Recommended allocation for institutional review:{" "}
          <b className="text-[#E4E4E7] print:text-black">
            {recommended.label} ({recommended.name})
          </b>
          .
        </p>
      </section>

      {/* Hero metric strip */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-2 print:break-inside-avoid" data-testid="hero-metrics">
        <MetricCard
          label="Net Profit · 1.00x"
          value={fmtUsd(CELLS["1.00x"].raw_pnl)}
          sub={`${pctReturn(CELLS["1.00x"]).toFixed(1)}% over ${REPORT_META.duration_years.toFixed(2)} yr`}
          kind="positive"
          testid="hero-net-1"
        />
        <MetricCard
          label="Net + 40% Rebate · 1.00x"
          value={fmtUsd(CELLS["1.00x"].eff_pnl)}
          sub={`CAGR ≈ ${cagrEffPct(CELLS["1.00x"]).toFixed(2)}%`}
          kind="positive"
          testid="hero-eff-1"
        />
        <MetricCard
          label="Profit Factor"
          value={CELLS["1.00x"].pf.toFixed(3)}
          sub="invariant across allocations"
          kind="highlight"
          testid="hero-pf"
        />
        <MetricCard
          label="Total Trades"
          value={CELLS["1.00x"].n_trades.toLocaleString()}
          sub={`Win rate ${fmtPct(CELLS["1.00x"].wr_pct, 1)}`}
          testid="hero-n"
        />
      </section>

      {/* Allocation detail rows */}
      <section className="space-y-3 print:break-inside-avoid" data-testid="allocation-detail">
        <h2 className="text-[13px] uppercase tracking-wider text-[#71717A] print:text-gray-700">
          Allocation modes — side by side
        </h2>
        {cells.map((c) => (
          <CellDetailRow key={c.label} cell={c} />
        ))}
      </section>

      {/* Yearly breakdown */}
      <section className="space-y-2 print:break-inside-avoid" data-testid="yearly-breakdown">
        <h2 className="text-[13px] uppercase tracking-wider text-[#71717A] print:text-gray-700">
          Annual PnL breakdown
        </h2>
        <YearlyTable />
        <div className="text-[10.5px] text-[#52525B] italic print:text-gray-500">
          PnL values are realised gross PnL per calendar year (2026 = YTD
          through {REPORT_META.window_end}). The "+40% Rebate" column folds
          in a 40% market-making fee rebate against the {fmtUsd2(CELLS["1.00x"].fees_usdt)} /{" "}
          {fmtUsd2(CELLS["0.50x"].fees_usdt)} fee totals across the window.
        </div>
      </section>

      {/* Quarterly Institutional Performance (1.00x only) */}
      <section className="space-y-2 print:break-inside-avoid" data-testid="quarterly-institutional">
        <h2 className="text-[13px] uppercase tracking-wider text-[#71717A] print:text-gray-700">
          Quarterly Institutional Performance · 1.00x
        </h2>
        <QuarterlyTable1x />
        <div className="text-[10.5px] text-[#52525B] italic print:text-gray-500">
          Per-quarter aggregation of the validated 1.00x MASTER backtest.
          Trades allocated by close-time. Win Rate = wins / total trades.
          Profit Factor = gross profit / |gross loss| (display "∞" when
          there are wins but no losses, "—" when there are no trades).
          Max DD is computed on the equity curve LOCAL to the quarter
          (peak reset at quarter start). 2026 Q1 is partial (window ends{" "}
          {REPORT_META.window_end}). Quarterly Net PnL sums match the
          annual totals exactly.
        </div>
      </section>

      {/* Annual Return on Capital (1.00x only) */}
      <section className="space-y-2 print:break-inside-avoid" data-testid="annual-roc">
        <h2 className="text-[13px] uppercase tracking-wider text-[#71717A] print:text-gray-700">
          Annual Return on Initial Capital · 1.00x
        </h2>
        <YearlyReturnOnCapitalTable1x />
        <div className="text-[10.5px] text-[#52525B] italic print:text-gray-500">
          Return on Capital = Net PnL / initial capital{" "}
          (${REPORT_META.initial_balance_usdt.toLocaleString()}). Simple
          annual return — NOT a compounding CAGR. The +40% Rebate column
          adds 40% of fees as a market-making rebate scenario. Max DD is
          computed on the equity curve local to the year. 2026 is partial
          (window ends {REPORT_META.window_end}).
        </div>
      </section>

      {/* Equity curves */}
      <section className="space-y-3 print:break-inside-avoid" data-testid="equity-curves">
        <h2 className="text-[13px] uppercase tracking-wider text-[#71717A] print:text-gray-700">
          Equity profile
        </h2>
        <EquityCurveSVG cell={CELLS["1.00x"]} />
        <EquityCurveSVG cell={CELLS["0.50x"]} />
      </section>

      {/* Risk section */}
      <section
        className="rounded-md border border-[#27272A] bg-[#0e1117] p-4 print:border-gray-300 print:bg-white print:break-inside-avoid"
        data-testid="risk-section"
      >
        <h2 className="text-[13px] uppercase tracking-wider text-[#71717A] mb-3 print:text-gray-700">
          Risk Profile
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {cells.map((c) => (
            <div
              key={c.label}
              className="rounded border border-[#27272A] bg-[#0a0d12] p-3 print:border-gray-300 print:bg-white"
              data-testid={`risk-card-${c.label.replace(".", "_")}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-[12px] font-semibold text-[#E4E4E7] print:text-black">
                  {c.label} · {c.name}
                </div>
                <span className="text-[10.5px] text-[#71717A] font-mono print:text-gray-600">
                  mult {c.multiplier.toFixed(2)}x
                </span>
              </div>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[12px] font-mono">
                <dt className="text-[#71717A] print:text-gray-600">Max drawdown</dt>
                <dd className="text-right text-[#f87171] print:text-black">{fmtPct(c.dd_pct, 2)}</dd>
                <dt className="text-[#71717A] print:text-gray-600">Drawdown (USD)</dt>
                <dd className="text-right text-[#f87171] print:text-black">{fmtUsd(-c.dd_usdt)}</dd>
                <dt className="text-[#71717A] print:text-gray-600">Recovery factor</dt>
                <dd className="text-right text-[#E4E4E7] print:text-black">{c.recovery_factor.toFixed(3)}</dd>
                <dt className="text-[#71717A] print:text-gray-600">Time underwater</dt>
                <dd className="text-right text-[#E4E4E7] print:text-black">{c.total_underwater_days.toFixed(0)} d</dd>
                <dt className="text-[#71717A] print:text-gray-600">Longest underwater span</dt>
                <dd className="text-right text-[#E4E4E7] print:text-black">{c.longest_underwater_days.toFixed(0)} d</dd>
                <dt className="text-[#71717A] print:text-gray-600">Coefficient of variation</dt>
                <dd className="text-right text-[#E4E4E7] print:text-black">
                  {(() => {
                    const v = coefVariation(c);
                    return v == null ? "—" : v.toFixed(2);
                  })()}
                </dd>
                <dt className="text-[#71717A] print:text-gray-600">Positive years</dt>
                <dd className="text-right text-[#E4E4E7] print:text-black">
                  {yearsPositive(c)} / {Object.keys(c.yearly).length}
                </dd>
                <dt className="text-[#71717A] print:text-gray-600">Worst year</dt>
                <dd className="text-right text-[#f87171] print:text-black">
                  {c.worst_year} · {fmtUsd(c.worst_year_pnl)}
                </dd>
                <dt className="text-[#71717A] print:text-gray-600">Worst month</dt>
                <dd className="text-right text-[#f87171] print:text-black">
                  {c.worst_month} · {fmtUsd(c.worst_month_pnl)}
                </dd>
              </dl>
            </div>
          ))}
        </div>
      </section>

      {/* Verdict */}
      <section
        className="rounded-md border border-[#065f46] bg-[#0a1612] p-4 print:border-gray-300 print:bg-white print:break-inside-avoid"
        data-testid="final-verdict"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 size={20} className="text-[#34d399] shrink-0 mt-0.5 print:text-black" />
          <div className="space-y-1.5">
            <div className="text-[14px] font-semibold text-[#E4E4E7] print:text-black">
              Final Verdict — ROBUST · PROFITABLE across allocation modes
            </div>
            <ul className="text-[12.5px] text-[#A0A0AB] leading-relaxed list-disc list-inside print:text-gray-800">
              <li>
                Profit factor <b className="text-[#E4E4E7] print:text-black">{CELLS["1.00x"].pf.toFixed(3)}</b>{" "}
                over <b>{CELLS["1.00x"].n_trades.toLocaleString()} trades</b> and a{" "}
                <b className="text-[#E4E4E7] print:text-black">
                  {REPORT_META.duration_years.toFixed(2)}-year contiguous window
                </b>{" "}
                — well above the 1.0 break-even threshold across both modes.
              </li>
              <li>
                Effective net return positive in both allocation modes
                ({fmtUsd(CELLS["1.00x"].eff_pnl)} at 1.00x and{" "}
                {fmtUsd(CELLS["0.50x"].eff_pnl)} at 0.50x), and{" "}
                <b className="text-[#34d399] print:text-black">
                  {yearsPositive(CELLS["1.00x"])} of {Object.keys(CELLS["1.00x"].yearly).length} calendar years
                </b>{" "}
                close in the green.
              </li>
              <li>
                The 0.50x institutional mode reduces max drawdown by{" "}
                <b className="text-[#34d399] print:text-black">
                  {(CELLS["1.00x"].dd_pct - CELLS["0.50x"].dd_pct).toFixed(1)} pp
                </b>{" "}
                (from {fmtPct(CELLS["1.00x"].dd_pct, 1)} to{" "}
                {fmtPct(CELLS["0.50x"].dd_pct, 1)}) while preserving the
                identical Profit Factor and Win Rate — the institutional-grade
                allocation for risk-aware deployment.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section
        className="rounded-md border border-[#27272A] bg-[#0a0d12] p-3 print:border-gray-300 print:bg-white print:break-inside-avoid"
        data-testid="disclaimer"
      >
        <div className="text-[10.5px] uppercase tracking-wider text-[#71717A] mb-1 print:text-gray-600">
          Disclosures
        </div>
        <p className="text-[11px] text-[#71717A] leading-relaxed print:text-gray-700">
          Figures are derived from a single contiguous backtest of historical
          {" "}{REPORT_META.asset} market data ({REPORT_META.window_start} →{" "}
          {REPORT_META.window_end}), executed against the V2 strategy at a
          fixed-USDT order-size scheme. Past performance is not indicative of
          future results. Fee rebate scenario assumes a 40% maker / market-
          making rebate applied against gross fees over the full window.
          Drawdown, time-underwater and recovery metrics are calculated on the
          backtest equity curve. Slippage of 2 bps and a fee rate of 6 bps
          per fill are applied to every trade.
        </p>
      </section>

      {/* Advanced — Raw PDFs (opt-in) */}
      <RawPdfsSection />

      {/* Footer */}
      <div className="text-[10px] text-[#52525B] text-center font-mono print:text-gray-500">
        Institutional Report · V2 Strategy · BTC/USDT · MASTER window {REPORT_META.window_start} → {REPORT_META.window_end} · validated {REPORT_META.generated_iso}
      </div>
    </div>
  );
}
